import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import _ from 'lodash';
import {
  PdfLoader,
  PdfHighlighter,
  Popup,
  AreaHighlight,
} from 'react-pdf-highlighter';

import ErrorBoundary from '../../ErrorBoundary';
import FileViewTip from './Tip';

import {
  fetchFileComments,
  fetchFileVersionComments,
  fetchShareFileComments,
  fetchShareFileVersionComments,
  setFileCommentHighlighted,
} from '../../../actions/Files';

import {
  APIupdateFileComment,
  APIupdateShareFileComment,
} from '../../../api/Files';

const FileViewPDF = ({
  file,
  fileVersion,
  comments,
  commentsView,
  commentHighlighted,
  commentsAnnotateMode,
  highlightComment,
  share,
  loadShareComments,
  loadComments,
  updateCommentsCount,
  handleSidebarTab,
  readOnly,
}) => {
  const [highlights, setHighlights] = useState([]);
  const [scrollViewerTo, setScrollViewerTo] = useState(() => () => {});
  const [cursor, setCursor] = useState(null);
  const [mouseDown, setMouseDown] = useState(false);
  const [preserveNewHighlightTip, setPreserveNewHighlightTip] = useState(false);

  const preserveNewHighlightTipRef = useRef(preserveNewHighlightTip);
  const highlightsRef = useRef(highlights);

  const newHighlightAwaiting = highlights.find(h => h.id === 'new');

  useEffect(() => {
    const updateCursor = (e) => {
      const cursorType = e.target.style ? e.target.style.cursor : '';
      setCursor(cursorType);
    };

    document.addEventListener('mousemove', updateCursor);

    return () => {
      document.removeEventListener('mousemove', updateCursor);
    };
  }, []);

  const setScrollRef = (scrollTo) => {
    setScrollViewerTo(() => scrollTo);
  };

  const getHighlightById = useCallback((id) => highlights.find(h => h.id === id), [highlights]);

  const scrollToHighlightFromHash = useCallback((comment) => {
    if (!comment) return;
    const highlight = getHighlightById(comment.commentId);

    if (highlight) {
      scrollViewerTo(highlight);
    }
  }, [getHighlightById, scrollViewerTo]);

  const resetScrollHighlight = useCallback(() => {
    scrollToHighlightFromHash(null);
  }, [scrollToHighlightFromHash]);

  useEffect(() => {
    if (commentHighlighted && commentHighlighted.type === 'document') {
      scrollToHighlightFromHash(commentHighlighted);
      highlightComment(null);
    }
  }, [commentHighlighted, highlightComment, scrollToHighlightFromHash]);

  useEffect(() => {
    const filteredHighlights = comments.filter(c => (
      c.type === 'pdf' && commentsView !== 'other' && !_.isEmpty(c.meta)
    )).map(c => ({
      position: c.meta.position,
      content: c.meta.content,
      comment: c.comment,
      user: c.user,
      id: c.id,
      created_at: c.created_at,
      updated_at: c.updated_at,
      replies: c.replies,
    }));
    setHighlights(filteredHighlights);
  }, [comments, commentsView]);

  useEffect(() => {
    const textLayer = document.querySelector('.textLayer');
    if (textLayer) {
      if (commentsAnnotateMode) {
        textLayer.classList.add('text-layer-annotate-mode-enabled');
      } else {
        textLayer.classList.remove('text-layer-annotate-mode-enabled');
      }
    }
  }, [commentsAnnotateMode, highlights]);

  const updateHighlight = (id, position, content) => {
    const updatedHighlights = highlightsRef.current.map(h => {
      if (h.id !== id) return h;

      const updatedHighlight = h;

      updatedHighlight.position = { ...updatedHighlight.position, ...position };
      updatedHighlight.content = { ...updatedHighlight.content, ...content };

      const meta = {
        position: updatedHighlight.position,
        content: updatedHighlight.content,
      };

      if (id === 'new') return updatedHighlight;

      if (share) {
        APIupdateShareFileComment(share.uuid, file.uuid, {
          id,
          meta,
        }).then(() => {
          loadShareComments(share.uuid, file.uuid, fileVersion, true);
        });
      } else {
        APIupdateFileComment({
          id,
          meta,
        }).then(() => {
          loadComments(file.uuid, fileVersion, true).then((commentsResponse) => {
            updateCommentsCount(commentsResponse.comments_count, fileVersion);
          });
        });
      }

      return updatedHighlight;
    });

    setHighlights(updatedHighlights);
  };

  const handleSelectionFinished = (position, content, hideTipAndSelection) => {
    if (content.text) return null;

    const newHighlights = highlightsRef.current.filter(h => h.id !== 'new');

    const newHighlight = {
      id: 'new',
      position,
      content,
      replies: [],
    };

    hideTipAndSelection();

    setPreserveNewHighlightTip(true);
    setHighlights([...newHighlights, newHighlight]);

    return (
      <FileViewTip
        file={file}
        fileVersion={fileVersion}
        type="pdf"
        meta={{ position, content }}
        hideTipAndSelection={hideTipAndSelection}
        updateCommentsCount={updateCommentsCount}
        handleSidebarTab={handleSidebarTab}
        readOnly={readOnly}
      />
    );
  };

  useEffect(() => {
    preserveNewHighlightTipRef.current = preserveNewHighlightTip;
  }, [preserveNewHighlightTip]);

  useEffect(() => {
    highlightsRef.current = highlights;
  }, [highlights]);

  return (
    <ErrorBoundary>
      {/* eslint-disable jsx-a11y/no-static-element-interactions */}
      <div
        className={classnames({ 'pdf-highlighter-wrapper-annotation-mode': commentsAnnotateMode })}
        onMouseDown={(e) => {
          setMouseDown(true);
          if (e.target.type !== 'button') {
            setPreserveNewHighlightTip(false);
          }
        }}
        onMouseUp={() => setMouseDown(false)}
      >
        {/* eslint-enable jsx-a11y/no-static-element-interactions */}
        <PdfLoader url={file.urls.preview}>
          {(pdfDocument) => (
            <PdfHighlighter
              pdfDocument={pdfDocument}
              highlights={highlights}
              enableAreaSelection={
                commentsAnnotateMode && (cursor === '' || mouseDown) ? () => true : null
              }
              scrollRef={setScrollRef}
              onScrollChange={resetScrollHighlight}
              pdfScaleValue="page-width"
              onSelectionFinished={handleSelectionFinished}
              highlightTransform={(
                highlight,
                index,
                setTip,
                hideTip,
                viewportToScaled,
                screenshot,
                isScrolledTo,
              ) => (
                <Popup
                  popupContent={(
                    <FileViewTip
                      file={file}
                      fileVersion={fileVersion}
                      highlight={highlight}
                      type="pdf"
                      meta={newHighlightAwaiting ?
                        {
                          position: newHighlightAwaiting.position,
                          content: newHighlightAwaiting.content,
                        }
                        : null}
                      updateCommentsCount={updateCommentsCount}
                      handleSidebarTab={handleSidebarTab}
                      readOnly={readOnly}
                    />
                  )}
                  onMouseOver={(popupContent) => {
                    if (!(preserveNewHighlightTipRef.current && highlight.id === 'new')) {
                      setPreserveNewHighlightTip(false);
                      setTip(highlight, () => popupContent);
                    }
                  }}
                  onMouseOut={() => {
                    if (!newHighlightAwaiting) {
                      hideTip();
                    }
                  }}
                  key={index}
                >
                  <AreaHighlight
                    isScrolledTo={isScrolledTo}
                    highlight={highlight}
                    onChange={(boundingRect) => {
                      updateHighlight(
                        highlight.id,
                        { boundingRect: viewportToScaled(boundingRect) },
                        { image: screenshot(boundingRect) },
                      );
                    }}
                    disableDragging={!commentsAnnotateMode}
                    enableResizing={commentsAnnotateMode}
                  />
                </Popup>
              )}
            />
          )}
        </PdfLoader>
      </div>
    </ErrorBoundary>
  );
};

const mapStateToProps = state => ({
  comments: state.Files.comments,
  commentsView: state.Files.commentsView,
  commentsOrder: state.Files.commentsOrder,
  commentHighlighted: state.Files.commentHighlighted,
  commentsAnnotateMode: state.Files.commentsAnnotateMode,
  share: state.Shares.share,
});

const mapDispatchToProps = dispatch => ({
  loadComments: (fileUuid, fileVersion, silent = false) => {
    if (fileVersion) {
      return dispatch(fetchFileVersionComments(fileUuid, fileVersion, silent));
    }
    return dispatch(fetchFileComments(fileUuid, silent));
  },
  loadShareComments: (shareUuid, fileUuid, fileVersion, silent = false) => {
    if (fileVersion) {
      return dispatch(fetchShareFileVersionComments(shareUuid, fileUuid, fileVersion, silent));
    }
    return dispatch(fetchShareFileComments(shareUuid, fileUuid, silent));
  },
  highlightComment: (commentHighlighted) => {
    dispatch(setFileCommentHighlighted(commentHighlighted));
  },
});

FileViewPDF.propTypes = {
  file: PropTypes.object.isRequired,
  fileVersion: PropTypes.number,
  comments: PropTypes.array.isRequired,
  commentsView: PropTypes.string.isRequired,
  commentHighlighted: PropTypes.object,
  commentsAnnotateMode: PropTypes.bool.isRequired,
  loadComments: PropTypes.func.isRequired,
  loadShareComments: PropTypes.func.isRequired,
  highlightComment: PropTypes.func.isRequired,
  share: PropTypes.object,
  updateCommentsCount: PropTypes.func.isRequired,
  handleSidebarTab: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

FileViewPDF.defaultProps = {
  fileVersion: null,
  commentHighlighted: null,
  share: null,
  readOnly: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(FileViewPDF);
